import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, FileText, Scale, Droplets, Receipt } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { getApiErrorMessage } from "../api/client";
import { useWeightTickets, useCreateWeightTicket } from "../data/truckingData";
import FleetUnitPicker from "../components/trucking/FleetUnitPicker";
import { useFleetUnits } from "../screens/trucking/FleetRegistryScreen";
import { PageHeaderBand } from "../components/layout/PageHeaderBand";
import { LoadFailureNotice } from "../components/ui/LoadFailureNotice";
import { queryFailureError, queryLoadFailure } from "../lib/load-failure";
import { unwrapArray } from "../lib/api-envelope";
import { formatInstantDay, todayCalendarDate, instantTenantDay } from "../lib/calendar-date";
import { useTenantTimeZone } from "../hooks/useTenantTimeZone";
import { fmtQty, parseMoney, sumMoney } from "../lib/display-labels";
import { displayValue } from "../features/fieldiq-handoff/contracts";

/**
 * The four commodities the create form offers. The form is a write and is
 * left as it was; the DISPLAY reads the full enum below.
 */
const COMMODITY_TYPES = [
  { id: "produced_water", label: "Produced water" },
  { id: "fresh_water", label: "Fresh water" },
  { id: "brine", label: "Brine" },
  { id: "waste_fluid", label: "Waste fluid" },
];

/**
 * Exhaustive over the `commodity_type` enum. The display used to know four of
 * its seven values and echo the raw value for the rest. An outline chip: a
 * commodity is a kind, not a state, so it claims no status hue.
 */
const COMMODITY_LABEL: Record<string, string> = {
  produced_water: "Produced water",
  fresh_water: "Fresh water",
  brine: "Brine",
  waste_fluid: "Waste fluid",
  crude_oil: "Crude oil",
  condensate: "Condensate",
  other: "Other",
};

/**
 * A weight ticket as GET /trucking/weight-tickets sends it: `wt.*` plus
 * `truck_unit_number` (joined from `trucking_fleet` since api#860) and
 * `customer_name`.
 *
 * WHAT THIS PAGE NO LONGER READS, AND WHY:
 * - `flagged`, `flagReason` and `status`: there are no such columns. The
 *   Flagged chip, the flagged sidebar and the Verified / Uninvoiced badges
 *   could never show anything. The real invoicing fact is `invoice_triggered`.
 * - a volume computed here from four hard-coded densities. The API stores
 *   `volume_m3`, computed with the ticket's own `density_kg_m3`; that is the
 *   figure shown. Recomputing it here was a second answer to one question.
 */
interface Ticket {
  id: string;
  wt_number: string | null;
  truck_unit_number: string | null;
  driver_name: string | null;
  commodity: string | null;
  source_lsd: string | null;
  destination: string | null;
  tare: number | null;
  gross: number | null;
  net: number | null;
  volume_m3: number | null;
  invoice_triggered: boolean | null;
  timestamp: string | null;
}

const str = (v: unknown): string | null => (v === null || v === undefined || v === "" ? null : String(v));

function toTicket(r: Record<string, unknown>): Ticket {
  return {
    id: String(r.id ?? ""),
    wt_number: str(r.wt_number),
    truck_unit_number: str(r.truck_unit_number),
    driver_name: str(r.driver_name),
    commodity: str(r.commodity),
    source_lsd: str(r.source_lsd),
    destination: str(r.destination),
    tare: parseMoney(r.tare_weight_kg),
    gross: parseMoney(r.gross_weight_kg),
    net: parseMoney(r.net_weight_kg),
    volume_m3: parseMoney(r.volume_m3),
    invoice_triggered: r.invoice_triggered === true ? true : r.invoice_triggered === false ? false : null,
    timestamp: str(r.timestamp),
  };
}

type ChipKey = "all" | "today" | "uninvoiced";
/**
 * Counts are tallies of the loaded list: GET /weight-tickets is unpaginated.
 * "Today" is the ticket's own instant read as a local calendar day. The old
 * Today and This-week chips filtered nothing, and "today's tickets" counted
 * every ticket ever written.
 */
const CHIPS: { key: ChipKey; label: string }[] = [
  { key: "all", label: "All" },
  { key: "today", label: "Today" },
  { key: "uninvoiced", label: "Not invoiced" },
];

const WeightTicketView = () => {
  const navigate = useNavigate();
  const timeZone = useTenantTimeZone();
  const [search, setSearch] = useState("");
  const [chip, setChip] = useState<ChipKey>("all");
  const [truckFilter, setTruckFilter] = useState("");
  const [commodityFilter, setCommodityFilter] = useState("");

  // New ticket form
  const [formTruck, setFormTruck] = useState("");
  const [formCommodity, setFormCommodity] = useState("");
  // Starts EMPTY. This field used to be seeded with a plausible-looking legal
  // subdivision, which an operator who never touched it would have submitted
  // verbatim onto a billing and compliance document — and `source_lsd` is
  // rendered on the weight-ticket PDF and baked into the invoice line item's
  // description at draft time, where it is frozen. The format mask on the input
  // is a placeholder, so it cannot be submitted.
  const [formSource, setFormSource] = useState("");
  const [formDest, setFormDest] = useState("");
  const [formTare, setFormTare] = useState("");
  const [formGross, setFormGross] = useState("");
  const [formScaleRef, setFormScaleRef] = useState("");

  // The truck filter lists fleet units by number, the same `trucking_fleet`
  // rows a ticket's `truck_unit_number` is joined from (api#860).
  const fleetQuery = useFleetUnits(false);
  const truckNumbers = Array.from(
    new Set((fleetQuery.data ?? []).map((u) => u.unit_number).filter((n): n is string => n != null)),
  );
  const createWt = useCreateWeightTicket();

  const ticketsQuery = useWeightTickets();
  const failed = queryLoadFailure(ticketsQuery) != null;
  const loading = ticketsQuery.isLoading && !failed;
  const tickets = useMemo(
    () => (ticketsQuery.data == null || failed ? undefined : unwrapArray<Record<string, unknown>>(ticketsQuery.data).map(toTicket)),
    [ticketsQuery.data, failed],
  );

  // "Today" is the TENANT's today, and a ticket's instant is classified by the
  // tenant's day it fell on (companies.time_zone) — so an evening ticket in
  // Alberta is today, not tomorrow, whatever zone the viewer's device is in.
  // With no zone the Today count and volume are dashes, not zero.
  const today = todayCalendarDate(timeZone);
  const tenantDay = (iso: string | null) => instantTenantDay(iso, timeZone);
  const match = (t: Ticket, k: ChipKey) =>
    k === "all"
      ? true
      : k === "today"
        ? today !== null && tenantDay(t.timestamp) === today
        : t.invoice_triggered === false;
  const countOf = (k: ChipKey): number | null =>
    tickets && !(k === "today" && today === null) ? tickets.filter((t) => match(t, k)).length : null;

  // Stored figures, summed all-or-nothing: one ticket with no volume and the total is a dash.
  const todays = (tickets ?? []).filter((t) => match(t, "today"));
  const todayVolume = tickets && today !== null ? sumMoney(todays.map((t) => t.volume_m3)) : null;

  const filtered = useMemo(() => {
    let result = (tickets ?? []).filter((t) => match(t, chip));
    if (search) {
      const s = search.toLowerCase();
      result = result.filter((t) => [t.wt_number, t.driver_name, t.truck_unit_number].some((v) => (v ?? "").toLowerCase().includes(s)));
    }
    if (truckFilter) result = result.filter((t) => t.truck_unit_number === truckFilter);
    if (commodityFilter) result = result.filter((t) => t.commodity === commodityFilter);
    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `match` closes over `today` only
  }, [tickets, search, truckFilter, commodityFilter, chip, today]);

  // Form
  const formTareNum = parseFloat(formTare.replace(/,/g, '')) || 0;
  const formGrossNum = parseFloat(formGross.replace(/,/g, '')) || 0;
  const formNet = formGrossNum - formTareNum;

  const handleSaveTicket = () => {
    // source_lsd and destination are TEXT NOT NULL with no default (migration
    // 0099) and the route rejects either being falsy with a 400. Refuse here
    // rather than supplying a value the operator did not enter.
    // The truck is OPTIONAL (#815): the API stores `truckId || null`. It is a
    // `trucking_fleet` unit id since api#860 moved the foreign key there.
    if (!formCommodity || !formSource.trim() || !formDest.trim() || formTareNum <= 0 || formGrossNum <= 0) {
      toast.error("Fill in commodity, source LSD, destination, and weights");
      return;
    }
    // POST /trucking/weight-tickets reads camelCase ONLY — `body.sourceLsd`,
    // `body.tareWeightKg`, `body.truckId`. There is no normalizing middleware,
    // so the snake_case keys this used to send were never read and every submit
    // 400'd on the required-field check. Reads from this route are still
    // snake_case (`SELECT wt.*`), so the list above is unchanged.
    createWt.mutate({
      ...(formTruck ? { truckId: formTruck } : {}),
      commodity: formCommodity,
      sourceLsd: formSource.trim(),
      destination: formDest.trim(),
      tareWeightKg: formTareNum,
      grossWeightKg: formGrossNum,
      scaleTicketRef: formScaleRef || null,
    }, {
      onSuccess: () => {
        toast.success("Weight ticket saved");
        setFormTruck(""); setFormCommodity(""); setFormSource(""); setFormDest("");
        setFormTare(""); setFormGross(""); setFormScaleRef("");
      },
      onError: (err) => toast.error(getApiErrorMessage(err, "Failed to save ticket")),
    });
  };

  const selectClass =
    "flex-1 rounded-fi-control border border-fieldiq-border bg-fieldiq-card px-3 py-2 text-sm text-fieldiq-text focus:border-fi-amber focus:outline-none";

  return (
    <div data-testid="weight-tickets-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow="Trucking"
        title="Weight Tickets"
        onBack={() => navigate(-1)}
        backLabel="Back"
        actions={
          // Export CSV is gone: it toasted "coming soon" and exported nothing.
          <button
            type="button"
            onClick={() => document.getElementById('new-ticket-form')?.scrollIntoView({ behavior: 'smooth' })}
            className="fi-action fi-action--standard fi-action--primary"
          >
            <Plus className="size-4" aria-hidden="true" />
            New ticket
          </button>
        }
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: "Tickets", sub: "All on record", tone: "neutral", loading, value: failed ? "—" : displayValue(countOf("all")) },
          { icon: <Scale className="size-4" aria-hidden="true" />, label: "Today", sub: "Written today", tone: "info", loading, value: failed ? "—" : displayValue(countOf("today")) },
          { icon: <Droplets className="size-4" aria-hidden="true" />, label: "Volume", sub: "Today, m³", tone: "success", loading, value: failed || todayVolume == null ? "—" : todayVolume.toLocaleString("en-US", { maximumFractionDigits: 1 }) },
          { icon: <Receipt className="size-4" aria-hidden="true" />, label: "Not Invoiced", sub: "No invoice yet", tone: "accent", loading, value: failed ? "—" : displayValue(countOf("uninvoiced")) },
        ]}
      />

      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] gap-4 2xl:grid 2xl:grid-cols-[1fr_320px]">
          <div className="min-w-0 space-y-4">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
              <div className="scrollbar-hide flex flex-1 gap-2 overflow-x-auto">
                {CHIPS.map((c) => {
                  const active = chip === c.key;
                  const n = countOf(c.key);
                  return (
                    <button
                      key={c.key}
                      type="button"
                      aria-pressed={active}
                      onClick={() => setChip(c.key)}
                      className={`inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-fi-pill px-4 py-1.5 text-xs font-extrabold transition ${
                        active
                          ? "bg-fi-amber text-fi-amber-ink"
                          : "border border-fieldiq-border bg-fieldiq-card text-fieldiq-muted hover:text-fieldiq-text"
                      }`}
                    >
                      {c.label}
                      {!loading && n != null && (
                        <span className={`tabular-nums ${active ? "text-fi-amber-ink" : "text-fieldiq-muted"}`}>{n}</span>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="relative lg:w-72">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fieldiq-muted" aria-hidden="true" />
                <input
                  placeholder="Search WT number, driver..."
                  aria-label="Search weight tickets"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-card py-1.5 pl-10 pr-3 text-sm text-fieldiq-text placeholder:text-fieldiq-muted focus:border-fi-amber focus:outline-none"
                />
              </div>
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <select aria-label="Filter by truck" value={truckFilter} onChange={(e) => setTruckFilter(e.target.value)} className={selectClass}>
                <option value="">All trucks</option>
                {truckNumbers.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
              <select aria-label="Filter by commodity" value={commodityFilter} onChange={(e) => setCommodityFilter(e.target.value)} className={selectClass}>
                <option value="">All commodities</option>
                {Object.entries(COMMODITY_LABEL).map(([id, label]) => (
                  <option key={id} value={id}>{label}</option>
                ))}
              </select>
            </div>

            <section
              aria-labelledby="wt-register-title"
              className="overflow-hidden rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-amber bg-fieldiq-card"
            >
              <header className="border-b border-fieldiq-border px-4 py-4">
                <p className="text-[0.65rem] font-black uppercase tracking-[0.14em] text-fieldiq-muted">Weight ticket register</p>
                <h2 id="wt-register-title" className="mt-1 text-base font-extrabold tracking-tight text-fieldiq-text">Scale tickets</h2>
              </header>
              {failed ? (
                <LoadFailureNotice error={queryFailureError(ticketsQuery)} subject="weight tickets" />
              ) : loading ? (
                <div className="space-y-2 p-4" aria-hidden="true">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-12 animate-pulse rounded-fi-control bg-fieldiq-dark/60" />
                  ))}
                </div>
              ) : filtered.length === 0 ? (
                <p className="px-4 py-12 text-center text-sm text-fieldiq-muted">
                  {(tickets ?? []).length === 0 ? "No weight tickets yet" : "No weight tickets match these filters"}
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px] text-left text-sm">
                    <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40">
                      <tr className="text-xs font-bold uppercase tracking-wider text-fieldiq-muted">
                        <th className="px-4 py-3">WT number</th>
                        <th className="px-4 py-3">Written</th>
                        <th className="px-4 py-3">Truck / driver</th>
                        <th className="px-4 py-3">Commodity / route</th>
                        <th className="px-4 py-3 text-right">Tare kg</th>
                        <th className="px-4 py-3 text-right">Gross kg</th>
                        <th className="px-4 py-3 text-right">Net kg</th>
                        <th className="px-4 py-3 text-right">Volume m³</th>
                        <th className="px-4 py-3">Invoice</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((t) => {
                        const negative = t.net != null && t.net < 0;
                        return (
                          <tr key={t.id} data-testid="wt-row" className="border-t border-fieldiq-border">
                            <td className="px-4 py-3 font-mono font-bold text-fieldiq-text">{t.wt_number ?? "—"}</td>
                            <td className="px-4 py-3 text-xs text-fieldiq-muted">{formatInstantDay(t.timestamp, timeZone) ?? "—"}</td>
                            <td className="px-4 py-3">
                              <p className="font-semibold text-fieldiq-text">{t.truck_unit_number ?? "—"}</p>
                              <p className="text-xs text-fieldiq-muted">{t.driver_name ?? "—"}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="mb-1 inline-flex whitespace-nowrap rounded-fi-pill border border-fieldiq-border px-2 py-0.5 text-[11px] font-bold text-fieldiq-text">
                                {(t.commodity && COMMODITY_LABEL[t.commodity]) || "—"}
                              </span>
                              <p className="text-xs text-fieldiq-muted">{t.source_lsd ?? "—"} → {t.destination ?? "—"}</p>
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-fieldiq-text">{fmtQty(t.tare)}</td>
                            <td className="px-4 py-3 text-right font-mono text-fieldiq-text">{fmtQty(t.gross)}</td>
                            <td className="px-4 py-3 text-right font-mono font-bold text-fieldiq-text">
                              {fmtQty(t.net)}
                              {negative && <p className="text-[11px] font-bold text-fieldiq-text">Negative net</p>}
                            </td>
                            <td className="px-4 py-3 text-right font-mono text-fieldiq-text">
                              {t.volume_m3 == null ? "—" : t.volume_m3.toLocaleString("en-US", { maximumFractionDigits: 1 })}
                            </td>
                            <td className="px-4 py-3">
                              {t.invoice_triggered === true ? (
                                <span className="inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-green px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none text-fi-signal-ink">Invoiced</span>
                              ) : t.invoice_triggered === false ? (
                                <span className="inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-amber px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none text-fi-signal-ink">Not yet</span>
                              ) : (
                                <span className="inline-flex rounded-fi-pill border border-black/20 bg-fi-signal-slate px-2.5 py-1 text-[0.68rem] font-black uppercase leading-none text-fi-signal-ink">{"—"}</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              {!failed && tickets && (
                <p className="border-t border-fieldiq-border px-4 py-3 text-xs text-fieldiq-muted">
                  Showing {filtered.length} of {tickets.length} weight tickets
                </p>
              )}
            </section>
          </div>

          {/* The create form: a write, left as it was apart from colour. */}
          {/* Shown at every width. Below 2xl it stacks under the register and "New
              ticket" scrolls to it; it was hidden below lg, which left that button
              doing nothing on a phone. */}
          <aside className="mt-4 2xl:mt-0" id="new-ticket-form" aria-label="New weight ticket">
            <div className="rounded-fi-panel border border-fieldiq-border bg-fieldiq-card p-4">
              <h3 className="mb-4 text-sm font-bold text-fieldiq-text">New weight ticket</h3>
              <div className="space-y-3">
              <div>
                <label htmlFor="wt-truck" className="mb-1 block text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Truck (optional)</label>
                <FleetUnitPicker
                  id="wt-truck"
                  purpose="weight-ticket"
                  value={formTruck}
                  onChange={setFormTruck}
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-fieldiq-text"
                />
              </div>
              <div>
                <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Commodity</p>
                <select
                  value={formCommodity}
                  onChange={(e) => setFormCommodity(e.target.value)}
                  className="w-full rounded-fi-control border border-fieldiq-border bg-fieldiq-dark px-3 py-2.5 text-sm text-fieldiq-text"
                >
                  <option value="">Select commodity</option>
                  {COMMODITY_TYPES.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Source LSD</p>
                <Input placeholder="00-00-000-00W0" value={formSource} onChange={(e) => setFormSource(e.target.value)} className="bg-fieldiq-dark border-fieldiq-border" />
              </div>
              <div>
                <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Destination</p>
                <Input placeholder="Pembina SWD" value={formDest} onChange={(e) => setFormDest(e.target.value)} className="bg-fieldiq-dark border-fieldiq-border" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Tare Weight (kg)</p>
                  <Input placeholder="18,240" value={formTare} onChange={(e) => setFormTare(e.target.value)} className="bg-fieldiq-dark border-fieldiq-border font-mono" />
                </div>
                <div>
                  <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Gross Weight (kg)</p>
                  <Input placeholder="52,180" value={formGross} onChange={(e) => setFormGross(e.target.value)} className="bg-fieldiq-dark border-fieldiq-border font-mono" />
                </div>
              </div>
              <div>
                <p className="mb-1 text-[9px] font-bold uppercase tracking-wider text-fieldiq-muted">Scale Ticket Ref</p>
                <Input placeholder="Scale house reference #" value={formScaleRef} onChange={(e) => setFormScaleRef(e.target.value)} className="bg-fieldiq-dark border-fieldiq-border" />
              </div>
              <Button
                className="h-11 w-full text-sm font-bold"
                onClick={handleSaveTicket}
                disabled={createWt.isPending}
              >
                {createWt.isPending ? "Saving..." : "Save ticket"}
              </Button>
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
};

export default WeightTicketView;
